import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, CircularProgress, Stack } from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Feature, Map as OlMap, View } from 'ol';
import { GeoJSON } from 'ol/format';
import { Point } from 'ol/geom';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import 'ol/ol.css';
import { Projection } from 'ol/proj';
import Static from 'ol/source/ImageStatic';
import VectorSource from 'ol/source/Vector';
import { Icon, Style } from 'ol/style';

import { PostgresFilePointer } from '@/api/data-contracts';
import spacePinPlaced from '@/assets/icons/space-pin-placed.svg';
import spacePin from '@/assets/icons/space-pin.svg';
import { useLocationsService } from '@/features/locations';
import { queryKeys } from '@/react-query/constants';

import { useUpdateSpaceGeometryMutation } from '../../api';
import { loadGeometryIntoSource } from './geometryUtils';

export interface FloorPlanCanvasHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  centerOnPin: () => void;
  save: () => void;
  clear: () => void;
}

interface Props {
  locationId: string;
  filePointer: PostgresFilePointer;
  spaceId?: string;
  initialGeometry?: string;
  placementModeActive?: boolean;
  readOnly?: boolean;
  onSaved?: () => void;
  onGeometryConfirmed?: (geometry: string) => void;
  onPinStateChange?: (hasPendingPin: boolean, hasExistingPin: boolean) => void;
  onSavingChange?: (isSaving: boolean) => void;
}

const PIN_STYLE = new Style({
  image: new Icon({ src: spacePin, width: 32, height: 32, anchor: [0.5, 1] }),
});

const PIN_PLACED_STYLE = new Style({
  image: new Icon({ src: spacePinPlaced, width: 42, height: 54, anchor: [0.5, 1] }),
});

const FloorPlanCanvas = forwardRef<FloorPlanCanvasHandle, Props>(
  (
    {
      locationId,
      filePointer,
      spaceId,
      initialGeometry,
      placementModeActive = false,
      readOnly = false,
      onSaved,
      onGeometryConfirmed,
      onPinStateChange,
      onSavingChange,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { downloadDocumentById } = useLocationsService();
    const { mutate: saveGeometryMutate, isLoading: isSaving } = useUpdateSpaceGeometryMutation();

    const mapDivRef = useRef<HTMLDivElement>(null);
    const olMapRef = useRef<OlMap | null>(null);
    const vectorSourceRef = useRef<VectorSource>(new VectorSource());
    const geojsonRef = useRef(new GeoJSON());

    const placementModeRef = useRef(placementModeActive);

    const [pendingGeometry, setPendingGeometry] = useState<object | null>(null);
    const [isMapReady, setIsMapReady] = useState(false);

    const hasExistingPin = !!initialGeometry;
    const hasPendingPin = pendingGeometry !== null;

    useEffect(() => {
      placementModeRef.current = placementModeActive;
    }, [placementModeActive]);

    useEffect(() => {
      onPinStateChange?.(hasPendingPin, hasExistingPin);
    }, [hasPendingPin, hasExistingPin, onPinStateChange]);

    const { data: imageBlob, isLoading: isLoadingImage } = useQuery<Blob>({
      queryKey: [queryKeys.floorPlanImage, locationId, filePointer.objectId],
      queryFn: () => downloadDocumentById(locationId, String(filePointer.objectId)),
      enabled: !!filePointer.objectId && !!locationId,
      staleTime: Infinity,
    });

    useEffect(() => {
      if (!imageBlob || !mapDivRef.current) return;

      const mapDiv = mapDivRef.current;
      let cancelled = false;
      let createdUrl: string | null = null;

      const init = async () => {
        const url = URL.createObjectURL(imageBlob);
        createdUrl = url;

        const dimensions = await new Promise<{ width: number; height: number }>((resolve) => {
          const img = new Image();
          img.onload = () => resolve({ width: img.width, height: img.height });
          img.src = url;
        });

        if (cancelled) return;

        const { width, height } = dimensions;
        const extent = [0, 0, width, height];
        const projection = new Projection({ code: 'floor-plan', units: 'pixels', extent });

        const vectorSource = new VectorSource();
        vectorSourceRef.current = vectorSource;

        const olMap = new OlMap({
          target: mapDiv,
          layers: [
            new ImageLayer({
              source: new Static({ url, projection, imageExtent: extent }),
            }),
            new VectorLayer({ source: vectorSource, style: PIN_STYLE }),
          ],
          view: new View({ projection, extent, showFullExtent: true }),
          controls: [],
        });

        olMap.getView().fit(extent);
        olMapRef.current = olMap;

        if (initialGeometry) {
          loadGeometryIntoSource(vectorSource, geojsonRef.current, initialGeometry);
          vectorSource.getFeatures().forEach((f) => f.setStyle(PIN_PLACED_STYLE));
        }

        if (!readOnly) {
          olMap.on('singleclick', (event) => {
            if (!placementModeRef.current) return;
            const coord = event.coordinate;
            vectorSource.clear();
            const feature = new Feature({ geometry: new Point(coord) });
            feature.setId('space-pin');
            feature.setStyle(PIN_PLACED_STYLE);
            vectorSource.addFeature(feature);
            setPendingGeometry(JSON.parse(geojsonRef.current.writeFeature(feature)));
          });
        }

        if (!cancelled) setIsMapReady(true);
      };

      init().catch(console.error);

      return () => {
        cancelled = true;
        if (createdUrl) URL.revokeObjectURL(createdUrl);
        olMapRef.current?.setTarget(undefined);
        olMapRef.current = null;
        setIsMapReady(false);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageBlob]);

    const saveGeometry = useCallback(() => {
      saveGeometryMutate(
        { spaceId: spaceId!, geometry: pendingGeometry ? JSON.stringify(pendingGeometry) : null },
        {
          onSuccess: () => {
            enqueueSnackbar(t('spaces_form.position_on_map.position_saved'), {
              variant: 'success',
            });
            setPendingGeometry(null);
            onSaved?.();
          },
          onError: () => {
            enqueueSnackbar(t('errors.something_went_wrong'), { variant: 'error' });
          },
        },
      );
    }, [saveGeometryMutate, spaceId, pendingGeometry, enqueueSnackbar, t, onSaved]);

    useEffect(() => {
      onSavingChange?.(isSaving);
    }, [isSaving, onSavingChange]);

    const clearPin = useCallback(() => {
      vectorSourceRef.current.clear();
      setPendingGeometry(null);
      if (initialGeometry) {
        loadGeometryIntoSource(vectorSourceRef.current, geojsonRef.current, initialGeometry);
        vectorSourceRef.current.getFeatures().forEach((f) => f.setStyle(PIN_PLACED_STYLE));
      }
    }, [initialGeometry]);

    useImperativeHandle(
      ref,
      () => ({
        zoomIn() {
          const view = olMapRef.current?.getView();
          if (!view) return;
          view.animate({ zoom: (view.getZoom() ?? 0) + 1, duration: 250 });
        },
        zoomOut() {
          const view = olMapRef.current?.getView();
          if (!view) return;
          view.animate({ zoom: (view.getZoom() ?? 0) - 1, duration: 250 });
        },
        centerOnPin() {
          const features = vectorSourceRef.current.getFeatures();
          if (features.length > 0 && olMapRef.current) {
            const geom = features[0].getGeometry();
            if (geom) {
              olMapRef.current.getView().fit(geom.getExtent(), {
                padding: [120, 120, 120, 120],
                duration: 300,
                maxZoom: 4,
              });
            }
          } else if (olMapRef.current) {
            const extent = olMapRef.current.getView().getProjection().getExtent();
            olMapRef.current.getView().fit(extent, { duration: 300 });
          }
        },
        save() {
          if (!hasPendingPin) return;
          if (spaceId && !isSaving) {
            saveGeometry();
          } else if (!spaceId && pendingGeometry) {
            onGeometryConfirmed?.(JSON.stringify(pendingGeometry));
            setPendingGeometry(null);
          }
        },
        clear: clearPin,
      }),
      [
        hasPendingPin,
        spaceId,
        isSaving,
        saveGeometry,
        clearPin,
        pendingGeometry,
        onGeometryConfirmed,
      ],
    );

    const cursor = readOnly ? 'default' : placementModeActive ? 'crosshair' : 'default';

    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {(isLoadingImage || !isMapReady) && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ position: 'absolute', inset: 0, zIndex: 1 }}
          >
            <CircularProgress size={36} />
          </Stack>
        )}

        <Box
          ref={mapDivRef}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
            overflow: 'hidden',
            cursor,
            visibility: isMapReady ? 'visible' : 'hidden',
          }}
        />
      </Box>
    );
  },
);

FloorPlanCanvas.displayName = 'FloorPlanCanvas';

export default FloorPlanCanvas;
